import React, { useCallback, useEffect, useState } from 'react';

import SupplierPortalSettingsFieldCreateModal from './SupplierPortalSettingsFieldCreateModal';
import SupplierPortalSettingsFieldEditModal from './SupplierPortalSettingsFieldEditModal';

import { truncateFileName } from 'Helpers/misc';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

import EditIcon from 'Assets/icons/edit.svg';
import EyeSlashIcon from 'Assets/icons/eye-slash.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import PlusIcon from 'Assets/icons/plus.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function SupplierPortalSettingsFields(props) {
    const [fields, setFields] = useState([]);
    const [editField, setEditField] = useState({});

    const fieldEditModal = useModal();
    const confirm = useConfirmationModal();
    const toast = useToast();

    useEffect(() => {
        setFields(props.fields);
    }, []);

    useEffect(() => {
        setFields(props.fields);
    }, [props.fields]);

    useEffect(() => {
        if (Object.keys(editField).length > 0) {
            fieldEditModal.show();
        }
    }, [editField]);

    const fieldCreateModal = useModal();

    const addField = () => {
        fieldCreateModal.show();
    };

    const clearFields = () => {
        setEditField({});
    };

    const toEdit = (field) => {
        setEditField(field);
    };
    const deleteField = (field) => {
        confirm({
            title: $t('Delete Field'),
            message: $t('Are you sure you want to delete {{name}}?', { name: field.name }) + ' ' + $t('Deleting the field will delete the documents too'),
            actions: [
                {
                    label: $t('Cancel'),
                    variant: 'secondary',
                },
                {
                    label: $t('Delete'),
                    variant: 'danger',
                    onClick: () => {
                        triggerDeleteField(field);
                    },
                },
            ],
        });
    };

    const togglePublish = (field) => {
        confirm({
            title: field.published ? $t('Unpublish Field') : $t('Publish Field'),
            message: field.published
                ? $t('Are you sure you want to make this action inaccessible to the supplier? Suppliers will no longer have access to the document.')
                : $t('Are you sure you want to make this action available to the supplier? The supplier will be notified by email.'),
            actions: [
                {
                    label: $t('Cancel'),
                    variant: 'secondary',
                },
                {
                    label: field.published ? $t('Unpublish') : $t('Publish'),
                    variant: 'primary',
                    onClick: () => {
                        triggerPublishField(field);
                    },
                },
            ],
        });
    };

    const triggerDeleteField = useCallback((field) => {
        axios
            .delete(props.deleteFieldUrl.replace('FIELD_ID', field.id), { data: {} })
            .then((response) => {
                props.fetchData();
                toast.success($t('The field deleted'));
            })
            .catch((e) => {
                toast.error($t('The field could not be deleted'));
                if (e?.response?.status === 403 && e?.response?.data?.message) {
                    toast.error(e.response.data.message);
                }
            });
    }, []);

    const triggerPublishField = useCallback((field) => {
        axios
            .put(props.toggleFieldUrl.replace('FIELD_ID', field.id), { data: {} })
            .then((response) => {
                props.fetchData();
                toast.success(field.published ? $t('The field unpublished') : $t('The field published'));
            })
            .catch((e) => {
                toast.error(field.published ? $t('The field could not be unpublished') : $t('The field could not be published'));
                if (e?.response?.status === 403 && e?.response?.data?.message) {
                    toast.error(e.response.data.message);
                }
            });
    }, []);

    return (
        <div className="table__card tw-space-y-6 tw-mt-4">
            <div className="tw-flex tw-justify-between tw-items-center">
                <h4 className="table__header">{$t('Fields')}</h4>
                <div>
                    <Button variant="primary" onClick={addField} disabled={props.request}>
                        <PlusIcon className="tw-w-3 tw-h-3" />
                        {$t('Add new Field')}
                    </Button>
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table inbud-table_first-sticky">
                    <thead>
                        <tr>
                            <th>{$t('Name')}</th>
                            <th>{$t('Instructions')}</th>
                            <th className="tw-w-40">{$t('Type')}</th>
                            <th className="tw-w-40">{$t('Status')}</th>
                            <th className="tw-w-80">{$t('Attachment')}</th>
                            <th className="tw-w-20">{$t('Published')}</th>
                            <th className="tw-w-52">{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((field) => {
                            return (
                                <tr key={field.uuid}>
                                    <td>{field.name}</td>
                                    <td>{field.instruction || '-'}</td>
                                    <td>{field.type_label}</td>
                                    <td>{field.state || '-'}</td>
                                    <td>
                                        {field.attachments.length > 0 && (
                                            <ul className="tw-space-y-2">
                                                {field.attachments.map((attachment) => {
                                                    return (
                                                        <li key={attachment.id}>
                                                            <a href={attachment.url} target="_blank" rel="noreferrer">
                                                                {truncateFileName(attachment.name)}
                                                            </a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </td>
                                    <td>{field.published ? $t('Yes') : $t('No')}</td>
                                    <td>
                                        <div className="tw-flex tw-items-center tw-gap-x-3">
                                            <TextButton variant="primary" onClick={() => togglePublish(field)}>
                                                {field.published ? <EyeSlashIcon className="tw-w-3 tw-h-3" /> : <EyeIcon className="tw-w-3 tw-h-3" />}
                                                {$t(field.published ? 'Unpublish' : 'Publish')}
                                            </TextButton>
                                            <TextButton variant="primary" onClick={() => toEdit(field)}>
                                                <EditIcon className="tw-w-3 tw-h-3" /> {$t('Edit')}
                                            </TextButton>
                                            <TextButton variant="danger" onClick={() => deleteField(field)}>
                                                <TrashIcon className="tw-w-3 tw-h-3" />
                                                {$t('Delete')}
                                            </TextButton>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <SupplierPortalSettingsFieldCreateModal
                id={'create-new-field'}
                modal={fieldCreateModal}
                closeHandler={() => {
                    fieldCreateModal.hide();
                    clearFields();
                }}
                successHandler={() => {
                    props.fetchData();
                    fieldCreateModal.hide();
                    clearFields();
                }}
                types={props.types}
                saveUrl={props.addFieldUrl}
                fetchData={props.fetchData}
                states={props.states}
            />

            <SupplierPortalSettingsFieldEditModal
                modal={fieldEditModal}
                closeHandler={() => {
                    fieldEditModal.hide();
                    clearFields();
                }}
                successHandler={() => {
                    props.fetchData();
                    fieldEditModal.hide();
                    clearFields();
                }}
                field={editField}
                saveUrl={props.updateFieldUrl}
                fetchData={props.fetchData}
                states={props.states}
                types={props.types}
                request={props.request}
            />
        </div>
    );
}
